import { LoaderCircle, RefreshCw } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import { useRegisterSW } from 'virtual:pwa-register/react'

import { Button } from '@/components/ui/button'
import { acompanharRegistro, useAtualizacao } from '@/lib/atualizacao'

/** Tablets ficam abertos dias seguidos: procura versão nova de tempos em tempos. */
const INTERVALO_VERIFICACAO_MS = 30 * 60_000
/** Voltar ao app também verifica, mas no máximo uma vez por minuto. */
const INTERVALO_MINIMO_MS = 60_000

/**
 * Registra o service worker (app instalável e aberto sem rede) e avisa quando
 * há versão nova: primeiro "baixando", depois "pronta" com o botão. Nada
 * recarrega sozinho no meio de um apontamento: a pessoa toca em "Atualizar"
 * quando puder (a fila offline sobrevive à recarga).
 */
export function AvisoAtualizacao() {
  const { baixando } = useAtualizacao()
  const {
    needRefresh: [temVersaoNova],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_url, registro) {
      if (!registro) return
      acompanharRegistro(registro)
      let ultima = Date.now()
      const verificar = () => {
        if (!navigator.onLine || Date.now() - ultima < INTERVALO_MINIMO_MS) return
        ultima = Date.now()
        void registro.update()
      }
      setInterval(verificar, INTERVALO_VERIFICACAO_MS)
      // App instalado reaberto pelo ícone volta da memória, sem nova navegação:
      // sem isto, a versão nova só seria notada no próximo intervalo.
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') verificar()
      })
      window.addEventListener('online', verificar)
    },
  })

  const mostrar = temVersaoNova ? 'pronta' : baixando ? 'baixando' : null
  return (
    <AnimatePresence>
      {mostrar && (
        <motion.div
          role="status"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 16 }}
          className="nao-imprimir fixed inset-x-0 bottom-4 z-[70] flex justify-center px-4"
        >
          <div className="flex min-h-11 items-center gap-3 rounded-2xl border border-brand/40 bg-surface py-2 pr-2 pl-4 shadow-card">
            {mostrar === 'pronta' ? (
              <>
                <RefreshCw className="size-4 shrink-0 text-brand" aria-hidden />
                <span className="text-sm font-medium">Nova versão do OP pronta</span>
                <Button size="sm" onClick={() => void updateServiceWorker(true)}>
                  Atualizar
                </Button>
              </>
            ) : (
              <>
                <LoaderCircle className="size-4 shrink-0 animate-spin text-brand" aria-hidden />
                <span className="pr-2 text-sm font-medium">Baixando nova versão do OP…</span>
              </>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
